import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MapPin, Clock, Star, Users, Compass, X, Calendar } from 'lucide-react';
import { useTranslation } from '@/hooks/useTranslation';
import TourAudioPlayer from '@/components/TourAudioPlayer';
import TourPlacesGallery from '@/components/TourPlacesGallery';
import { useTourPlaces } from '@/hooks/useTourPlaces';
import { generateTourSlug } from '@/lib/tourHelpers';

interface TourDetailViewProps {
  tour: {
    id: string;
    title: string;
    description?: string;
    city: string;
    country: string;
    duration: string;
    style: string;
    museum_count: number;
    created_at: string;
    latitude?: number;
    longitude?: number;
    name_places?: string[] | any;
  };
  onClose: () => void;
  onViewOnMap?: () => void;
}
const TourDetailView: React.FC<TourDetailViewProps> = ({
  tour,
  onClose,
  onViewOnMap
}) => {
  const {
    t
  } = useTranslation();

  // Extract place names from the tour
  const placeNames = React.useMemo(() => {
    if (!tour.name_places) return null;
    if (Array.isArray(tour.name_places)) return tour.name_places;
    if (typeof tour.name_places === 'object') {
      return Object.values(tour.name_places).filter((name): name is string => typeof name === 'string');
    }
    return null;
  }, [tour.name_places]);
  const {
    places,
    loading
  } = useTourPlaces(placeNames);
  const getStyleIcon = (style: string) => {
    switch (style) {
      case 'must-do':
        return <Star className="w-5 h-5" />;
      case 'best-rated':
        return <Users className="w-5 h-5" />;
      case 'off-the-beaten-path':
        return <Compass className="w-5 h-5" />;
      default:
        return <MapPin className="w-5 h-5" />;
    }
  };
  const getStyleLabel = (style: string) => {
    switch (style) {
      case 'must-do':
        return t('mustDo');
      case 'best-rated':
        return t('bestRated');
      case 'off-the-beaten-path':
        return t('offTheBeatenPath');
      default:
        return style;
    }
  };
  const getStyleDescription = (style: string) => {
    switch (style) {
      case 'must-do':
        return t('mustDoDescription');
      case 'best-rated':
        return t('bestRatedDescription');
      case 'off-the-beaten-path':
        return t('offTheBeatenPathDescription');
      default:
        return t('customTour');
    }
  };
  const getStyleColor = (style: string) => {
    switch (style) {
      case 'must-do':
        return 'bg-gradient-to-r from-red-500 to-red-600 text-white';
      case 'best-rated':
        return 'bg-gradient-to-r from-blue-500 to-blue-600 text-white';
      case 'off-the-beaten-path':
        return 'bg-gradient-to-r from-green-500 to-green-600 text-white';
      default:
        return 'bg-gradient-to-r from-gray-500 to-gray-600 text-white';
    }
  };
  return <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[200] flex items-center justify-center p-4" onClick={e => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }}>
      <Card className="w-full max-w-lg md:max-w-xl max-h-[85vh] md:max-h-[90vh] overflow-y-auto scrollbar-hide bg-background/95 backdrop-blur-md rounded-2xl border border-border/50 shadow-xl">
        <CardHeader className="relative">
          <Button variant="ghost" size="sm" onClick={onClose} className="absolute right-2 top-2 w-8 h-8 rounded-full">
            <X className="w-4 h-4" />
          </Button>
          
          <div className="pr-8">
            <CardTitle className="text-2xl font-bold leading-tight mb-4">
              {tour.title}
            </CardTitle>
          </div>
        </CardHeader>

        <CardContent className="space-y-6">
          {/* Audio Player */}
          <TourAudioPlayer tour={tour} />
          
          {/* Places Gallery */}
          <TourPlacesGallery places={places} loading={loading} />
          
          {/* Tour Description */}
          {tour.description && <div>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap break-words text-sm md:text-base">
                {tour.description}
              </p>
            </div>}

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-4 border-t">
            
            
            <Link to={`/tours/${generateTourSlug(tour.style, tour.city)}`} className="w-full">
              <Button variant="outline" className="w-full" size="lg">
                {t('tourPageViewFullPage')}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>;
};
export default TourDetailView;